/**
 * SOJ Core Modern - Main JavaScript Entry Point
 *
 * This file is ready for site-specific JavaScript code.
 * All core functionality is handled by core.js and its modules.
 *
 * @package SOJ_Core_Modern
 * @since 2.0.0
 * @author Son of Jesse
 */

// Import core functionality (this will initialize all modules)
import core from "./core.js";

// Import utilities if needed for site-specific code
import { utils, events, dom, log } from "./modules/utilities.js";

// Import newsletter modal
import { initNewsletterModal } from "./modules/newsletter-modal.js";

// Import animation systems
import AnimationSystem from "./modules/animations.js";
import GSAPAnimationSystem from "./modules/gsap-animations.js";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Observer } from "gsap/Observer";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(Observer);

// Initialize animation systems
let animationSystem;
let gsapAnimationSystem;

document.addEventListener("DOMContentLoaded", function () {

  // Initialize newsletter modal
  initNewsletterModal();

  // Initialize scroll-triggered animations (CSS-based)
  animationSystem = new AnimationSystem();

  // Initialize GSAP animations
  gsapAnimationSystem = new GSAPAnimationSystem();

  // Make animation systems available globally for dynamic content
  window.SOJ_ANIMATIONS = animationSystem;
  window.SOJ_GSAP_ANIMATIONS = gsapAnimationSystem;

  // Expose GSAP, ScrollTrigger, and Observer globally for block scripts
  window.gsap = gsap;
  window.ScrollTrigger = ScrollTrigger;
  window.Observer = Observer;

  // Initialize GSAP carousels (replaces Glide.js)
  initGSAPCarousels();

  console.log("[SOJ] Animation systems loaded (CSS + GSAP)");
});

/**
 * Initialize GSAP-based carousels
 */
function initGSAPCarousels() {
  const carouselWrappers = document.querySelectorAll(".carousel-wrapper");

  carouselWrappers.forEach((wrapper, wrapperIndex) => {
    // Add a unique identifier to this carousel wrapper
    const carouselId = `carousel-${wrapperIndex}`;
    wrapper.setAttribute("data-carousel-id", carouselId);

    const slidesContainer = wrapper.querySelector(".carousel-slides");
    let slides = Array.from(wrapper.querySelectorAll(".carousel-slide"));

    // Filter out empty slides (slides with no content or only whitespace)
    slides = slides.filter((slide) => {
      const textContent = slide.textContent.trim();
      const hasElements = slide.querySelectorAll("*").length > 0;
      return textContent.length > 0 || hasElements;
    });

    // Look for arrows inside wrapper first, then check previous siblings
    // Arrows are always rendered in carousel-start block (either in slider_arrows_wrapper or large_title_container)
    let prevBtn = wrapper.querySelector(".carousel-arrow--prev");
    let nextBtn = wrapper.querySelector(".carousel-arrow--next");
    let textLinks = [];
    let arrowsWrapper = null;
    let titleContainer = null;

    // If arrows not found in wrapper, check previous siblings
    // Arrows will always be before the carousel-wrapper (in carousel-start block)
    // Arrows should control the carousel that comes AFTER them
    if (!prevBtn || !nextBtn) {
      // Check previous siblings (could be slider_arrows_wrapper or large_title_container)
      // Both can exist for the same carousel (e.g., text links in slider_arrows_wrapper, arrows in large_title_container)
      let currentSibling = wrapper.previousElementSibling;
      while (currentSibling) {
        // Stop if we hit another carousel-wrapper or carousel-start/end block
        // This ensures arrows only control the carousel immediately after them
        if (
          currentSibling.classList.contains("carousel-wrapper") ||
          currentSibling.classList.contains("carousel-start") ||
          currentSibling.classList.contains("carousel-end")
        ) {
          break; // Stop searching, this belongs to another carousel
        }
        
        // Check for slider_arrows_wrapper (may contain text links or arrows)
        // Text links will only ever be inside slider_arrows_wrapper
        if (currentSibling.classList.contains("slider_arrows_wrapper")) {
          // Only use these arrows/text links if they don't already belong to another carousel
          const existingCarouselId = currentSibling.getAttribute("data-carousel-id");
          if (!existingCarouselId) {
            arrowsWrapper = currentSibling;
            // Link this arrows wrapper to THIS carousel (the one that comes after)
            arrowsWrapper.setAttribute("data-carousel-id", carouselId);
            // Get arrows if they exist here
            if (!prevBtn) prevBtn = currentSibling.querySelector(".carousel-arrow--prev") || prevBtn;
            if (!nextBtn) nextBtn = currentSibling.querySelector(".carousel-arrow--next") || nextBtn;
            // Get text links from this section (text links only exist in slider_arrows_wrapper)
            const siblingTextLinks = Array.from(
              currentSibling.querySelectorAll(".slide-text-link")
            );
            textLinks = [...textLinks, ...siblingTextLinks];
          }
        }
        // Check for large_title_container (may contain arrows)
        if (currentSibling.classList.contains("large_title_container")) {
          // Only use these arrows if they don't already belong to another carousel
          const existingCarouselId = currentSibling.getAttribute("data-carousel-id");
          if (!existingCarouselId) {
            titleContainer = currentSibling;
            // Link this title container to THIS carousel (the one that comes after)
            titleContainer.setAttribute("data-carousel-id", carouselId);
            // Get arrows if they exist here
            if (!prevBtn) prevBtn = currentSibling.querySelector(".carousel-arrow--prev") || prevBtn;
            if (!nextBtn) nextBtn = currentSibling.querySelector(".carousel-arrow--next") || nextBtn;
          }
        }
        
        // Stop searching if we found both arrows
        if (prevBtn && nextBtn) {
          break;
        }
        
        currentSibling = currentSibling.previousElementSibling;
      }
    } else {
      // Also check for text links inside wrapper
      textLinks = Array.from(wrapper.querySelectorAll(".slide-text-link"));
    }

    // Filter text links to only those associated with this carousel
    // Text links will only ever be inside slider_arrows_wrapper, and should only control the carousel after them
    textLinks = textLinks.filter((link) => {
      // Text links are always in slider_arrows_wrapper
      const linkWrapper = link.closest(".slider_arrows_wrapper");
      if (linkWrapper) {
        const wrapperCarouselId = linkWrapper.getAttribute("data-carousel-id");
        // Only include links that belong to THIS carousel (the one that comes after the wrapper)
        return wrapperCarouselId === carouselId;
      }
      // If link is inside the carousel wrapper itself, it belongs to this carousel
      return wrapper.contains(link);
    });

    const bulletsContainer = wrapper.querySelector(".carousel-bullets");
    let bullets = wrapper.querySelectorAll(".carousel-bullet");

    if (slides.length === 0) return;

    // Generate bullets dynamically if they don't exist
    // Only create bullets for non-empty slides
    if (bulletsContainer && bullets.length === 0 && slides.length > 1) {
      bulletsContainer.innerHTML = "";
      slides.forEach((_, index) => {
        const bullet = document.createElement("button");
        bullet.className = "carousel-bullet";
        bullet.setAttribute("aria-label", `Go to slide ${index + 1}`);
        if (index === 0) bullet.classList.add("active");
        bulletsContainer.appendChild(bullet);
      });
      bullets = bulletsContainer.querySelectorAll(".carousel-bullet");
    }

    // If only one slide, hide navigation
    if (slides.length <= 1) {
      if (prevBtn) prevBtn.style.display = "none";
      if (nextBtn) nextBtn.style.display = "none";
      if (bulletsContainer) bulletsContainer.style.display = "none";
      textLinks.forEach((link) => {
        link.style.display = "none";
      });
    }

    // Initialize text links active state
    if (textLinks.length > 0) {
      updateTextLinks(0);
    }

    // Measure height of first slide to set initial container height
    // We'll adjust dynamically when transitioning to other slides
    let initialHeight = 0;
    const firstSlide = slides[0];

    if (firstSlide) {
      // Temporarily make first slide visible and in normal flow to measure
      const originalPosition = firstSlide.style.position;
      const originalOpacity = firstSlide.style.opacity;
      const originalVisibility = firstSlide.style.visibility;
      const originalTransform = firstSlide.style.transform;

      // Make visible and in flow to measure
      firstSlide.style.position = "relative";
      firstSlide.style.opacity = "1";
      firstSlide.style.visibility = "visible";
      firstSlide.style.transform = "none";

      // Force reflow to ensure height is calculated
      firstSlide.offsetHeight;

      initialHeight = firstSlide.offsetHeight || firstSlide.scrollHeight;

      // Restore original styles (will be set to absolute below)
      firstSlide.style.position = originalPosition;
      firstSlide.style.opacity = originalOpacity;
      firstSlide.style.visibility = originalVisibility;
      firstSlide.style.transform = originalTransform;
    }

    // Set container height to first slide's height
    if (slidesContainer && initialHeight > 0) {
      slidesContainer.style.height = `${initialHeight}px`;
    }

    // Now make all slides absolutely positioned and restore initial states
    slides.forEach((slide, index) => {
      gsap.set(slide, {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
      });

      // Set initial positions for fade transitions
      if (index === 0) {
        // First slide should be visible
        gsap.set(slide, { x: 0, opacity: 1, visibility: "visible" });
      } else {
        // Other slides should be hidden (faded out)
        gsap.set(slide, { x: 0, opacity: 0, visibility: "hidden" });
      }
    });

    let currentIndex = 0;
    let resizeObserver = null;
    let heightUpdateAnimation = null;
    let autoplayInterval = null;
    const autoplayDelay = 3000; // 3 seconds between slides

    // Reset animations for elements in a slide
    function resetSlideAnimations(slide) {
      if (!slide) return;
      
      // Find carousel-circles blocks and reset their animations directly
      const carouselCirclesBlocks = slide.querySelectorAll('.carousel-circles');
      carouselCirclesBlocks.forEach(block => {
        if (block._resetCarouselCirclesAnimation) {
          block._resetCarouselCirclesAnimation();
        }
      });
      
      // Reset GSAP ScrollTrigger animations
      const gsapAnimatedElements = slide.querySelectorAll('.gsap-fade-in, .gsap-slide-left, .gsap-slide-right, .gsap-slide-up, .gsap-scale, .gsap-stagger, .gsap-rotate, .gsap-bounce, .gsap-elastic, .gsap-back');
      gsapAnimatedElements.forEach(element => {
        // Clear any GSAP properties and reset
        gsap.set(element, { clearProps: 'all' });
        // Refresh ScrollTrigger to update positions
        ScrollTrigger.refresh();
      });
      
      // Remove in-view class from CSS animated elements
      // This is safe to do here because the slide is already fading out
      const animatedElements = slide.querySelectorAll('.animate, [data-animate-on-slide]');
      animatedElements.forEach(element => {
        element.classList.remove('in-view');
      });
      
      // Dispatch custom event for blocks to reset their animations
      slide.dispatchEvent(new CustomEvent('slideReset', { bubbles: true }));
    }

    // Trigger animations for elements in a slide
    function triggerSlideAnimations(slide) {
      if (!slide) return;
      
      // Set initial state for CSS animated elements while slide is still transitioning
      const animatedElements = slide.querySelectorAll('.animate, [data-animate-on-slide]');
      animatedElements.forEach(element => {
        // Ensure element is in initial state (opacity: 0) before animation
        // But do this while the slide itself is still fading in, so no flicker
        element.classList.remove('in-view');
        // Force reflow to ensure state is reset
        void element.offsetHeight;
      });
      
      // Reset GSAP animations to initial state
      const gsapAnimatedElements = slide.querySelectorAll('.gsap-fade-in, .gsap-slide-left, .gsap-slide-right, .gsap-slide-up, .gsap-scale, .gsap-stagger, .gsap-rotate, .gsap-bounce, .gsap-elastic, .gsap-back');
      gsapAnimatedElements.forEach(element => {
        // Reset to initial state
        if (element.classList.contains('gsap-fade-in')) {
          gsap.set(element, { opacity: 0, y: 20 });
        } else if (element.classList.contains('gsap-slide-left')) {
          gsap.set(element, { opacity: 0, x: -100 });
        } else if (element.classList.contains('gsap-slide-right')) {
          gsap.set(element, { opacity: 0, x: 100 });
        } else if (element.classList.contains('gsap-slide-up')) {
          gsap.set(element, { opacity: 0, y: 100 });
        } else if (element.classList.contains('gsap-scale')) {
          gsap.set(element, { opacity: 0, scale: 0.8 });
        } else {
          gsap.set(element, { opacity: 0 });
        }
        
        // Kill any existing ScrollTrigger to prevent conflicts
        if (window.SOJ_GSAP_ANIMATIONS && window.SOJ_GSAP_ANIMATIONS.animations) {
          const animation = window.SOJ_GSAP_ANIMATIONS.animations.get(element);
          if (animation && animation.scrollTrigger) {
            animation.scrollTrigger.kill(false); // Kill but don't revert
          }
        }
      });
      
      // Find carousel-circles blocks and trigger their animations directly
      const carouselCirclesBlocks = slide.querySelectorAll('.carousel-circles');
      carouselCirclesBlocks.forEach(block => {
        const tryTrigger = (attempt = 0) => {
          if (block._triggerCarouselCirclesAnimation) {
            setTimeout(() => {
              block._triggerCarouselCirclesAnimation();
            }, 50);
          } else if (attempt < 10) {
            // Retry up to 10 times (1 second total) in case block hasn't initialized yet
            setTimeout(() => tryTrigger(attempt + 1), 100);
          }
        };
        tryTrigger();
      });
      
      // Wait for slide transition to complete before triggering animations
      // This ensures no flicker as the slide fades in
      setTimeout(() => {
        // Add in-view class to trigger CSS animations
        animatedElements.forEach(element => {
          element.classList.add('in-view');
        });
        
        // Manually trigger GSAP animations
        gsapAnimatedElements.forEach(element => {
          // Create and play animation directly instead of relying on ScrollTrigger
          let animation;
          
          if (element.classList.contains('gsap-fade-in')) {
            animation = gsap.to(element, {
              opacity: 1,
              y: 0,
              duration: 1,
              ease: "power2.out"
            });
          } else if (element.classList.contains('gsap-slide-left')) {
            animation = gsap.to(element, {
              opacity: 1,
              x: 0,
              duration: 1,
              ease: "power2.out"
            });
          } else if (element.classList.contains('gsap-slide-right')) {
            animation = gsap.to(element, {
              opacity: 1,
              x: 0,
              duration: 1,
              ease: "power2.out"
            });
          } else if (element.classList.contains('gsap-slide-up')) {
            animation = gsap.to(element, {
              opacity: 1,
              y: 0,
              duration: 1,
              ease: "power2.out"
            });
          } else if (element.classList.contains('gsap-scale')) {
            animation = gsap.to(element, {
              opacity: 1,
              scale: 1,
              duration: 1,
              ease: "back.out(1.7)"
            });
          } else {
            // Default fade in
            animation = gsap.to(element, {
              opacity: 1,
              duration: 1,
              ease: "power2.out"
            });
          }
          
          // Store animation for potential cleanup
          if (window.SOJ_GSAP_ANIMATIONS && window.SOJ_GSAP_ANIMATIONS.animations) {
            window.SOJ_GSAP_ANIMATIONS.animations.set(element, animation);
          }
        });
        
        // Dispatch custom event for blocks to trigger their animations
        slide.dispatchEvent(new CustomEvent('slideActive', { bubbles: true }));
      }, 50);
    }

    // Update container height based on current slide
    function updateContainerHeight(animate = true) {
      const currentSlide = slides[currentIndex];
      if (!currentSlide || !slidesContainer) return;

      // Measure current slide height
      const currentHeight =
        currentSlide.offsetHeight || currentSlide.scrollHeight;

      if (animate && heightUpdateAnimation) {
        // Kill any existing height animation
        heightUpdateAnimation.kill();
      }

      if (animate) {
        // Animate height change smoothly
        heightUpdateAnimation = gsap.to(slidesContainer, {
          height: currentHeight,
          duration: 0.3,
          ease: "power2.inOut",
        });
      } else {
        // Set height immediately (for initial setup)
        slidesContainer.style.height = `${currentHeight}px`;
      }
    }

    // Setup ResizeObserver to watch for height changes in current slide
    function setupHeightObserver() {
      // Clean up existing observer if any
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }

      const currentSlide = slides[currentIndex];
      if (!currentSlide) return;

      // Create ResizeObserver to watch for height changes
      resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          // Only update if this is the current slide
          if (entry.target === currentSlide) {
            const newHeight = entry.contentRect.height;
            if (newHeight > 0 && newHeight !== slidesContainer.offsetHeight) {
              // Animate height change smoothly
              if (heightUpdateAnimation) {
                heightUpdateAnimation.kill();
              }
              heightUpdateAnimation = gsap.to(slidesContainer, {
                height: newHeight,
                duration: 0.3,
                ease: "power2.inOut",
              });
            }
          }
        }
      });

      // Observe the current slide
      resizeObserver.observe(currentSlide);
    }

    // Update active bullet
    function updateBullets(index) {
      bullets.forEach((bullet, i) => {
        bullet.classList.toggle("active", i === index);
      });
    }

    // Update active text link
    function updateTextLinks(slideIndex) {
      // First, try to find a text link that matches the current slide index
      let activeLinkIndex = -1;
      
      textLinks.forEach((link, i) => {
        const linkSlideIndex = parseInt(link.getAttribute("data-slide-index")) || i;
        if (linkSlideIndex === slideIndex) {
          activeLinkIndex = i;
        }
      });
      
      // If no exact match, find the last text link with a slide index <= current slide index
      if (activeLinkIndex === -1) {
        let lastMatchingIndex = -1;
        textLinks.forEach((link, i) => {
          const linkSlideIndex = parseInt(link.getAttribute("data-slide-index")) || i;
          if (linkSlideIndex <= slideIndex) {
            lastMatchingIndex = i;
          }
        });
        
        // If we found a matching link, use it; otherwise keep the last text link active
        activeLinkIndex = lastMatchingIndex !== -1 ? lastMatchingIndex : textLinks.length - 1;
      }
      
      // Update active state
      textLinks.forEach((link, i) => {
        link.classList.toggle("active", i === activeLinkIndex);
      });
    }

    // Update arrow states (disable at first/last)
    function updateArrows() {
      if (prevBtn) {
        prevBtn.disabled = currentIndex === 0;
        prevBtn.classList.toggle("disabled", currentIndex === 0);
      }
      if (nextBtn) {
        nextBtn.disabled = currentIndex === slides.length - 1;
        nextBtn.classList.toggle(
          "disabled",
          currentIndex === slides.length - 1
        );
      }
    }

    // Helper function to measure slide height
    function measureSlideHeight(slide) {
      // Temporarily make slide visible and in normal flow to measure
      const originalPosition = slide.style.position;
      const originalOpacity = slide.style.opacity;
      const originalVisibility = slide.style.visibility;
      const originalTransform = slide.style.transform;
      const originalX = slide.style.x;

      // Set to relative position to measure height
      gsap.set(slide, {
        position: "relative",
        opacity: "1",
        visibility: "visible",
        transform: "none",
        x: 0,
      });

      // Force reflow to ensure height is calculated
      slide.offsetHeight;

      // Measure the height
      const height = slide.offsetHeight || slide.scrollHeight;

      // Restore original styles
      gsap.set(slide, {
        position: originalPosition || "absolute",
        opacity: originalOpacity || "0",
        visibility: originalVisibility || "hidden",
        transform: originalTransform || "none",
        x: originalX || "0",
      });

      return height;
    }

    // Go to specific slide
    function goToSlide(index) {
      // Prevent going beyond boundaries (no wrap-around)
      if (index < 0) return;
      if (index >= slides.length) return;

      // Don't animate if it's the same slide
      if (index === currentIndex) return;

      const currentSlide = slides[currentIndex];
      const nextSlide = slides[index];

      // Measure the next slide's height
      const nextSlideHeight = measureSlideHeight(nextSlide);

      // Get current container height
      const currentHeight =
        slidesContainer.offsetHeight || slidesContainer.scrollHeight;

      // Set initial position for next slide (centered, but hidden)
      gsap.set(nextSlide, { x: 0, opacity: 0, visibility: "visible" });

      // Animate both slides and container height simultaneously
      const tl = gsap.timeline();

      // Animate container height to next slide's height
      tl.to(
        slidesContainer,
        {
          height: nextSlideHeight,
          duration: 0.4,
          ease: "power2.inOut",
        },
        0
      );

      // Reset animations for current slide - do this after slide starts fading
      // to prevent flicker. The slide fade will handle the visual transition.

      // Current slide fades out
      tl.to(
        currentSlide,
        {
          opacity: 0,
          duration: 0.4,
          ease: "power2.inOut",
          onUpdate: function() {
            // Remove in-view class when slide opacity is low enough to prevent flicker
            // This happens during the fade, so elements fade smoothly with the slide
            if (this.progress() > 0.3 && !this._animationsReset) {
              this._animationsReset = true;
              const animatedElements = currentSlide.querySelectorAll('.animate, [data-animate-on-slide]');
              animatedElements.forEach(element => {
                element.classList.remove('in-view');
              });
            }
          },
          onComplete: () => {
            // Hide current slide after fade out and do final reset
            gsap.set(currentSlide, { visibility: "hidden" });
            resetSlideAnimations(currentSlide);
          },
        },
        0
      );

      // Next slide fades in at the same time
      tl.to(
        nextSlide,
        {
          opacity: 1,
          duration: 0.4,
          ease: "power2.inOut",
        },
        0
      );

      currentIndex = index;
      updateBullets(currentIndex);
      updateTextLinks(currentIndex);
      updateArrows();

      // Disconnect old observer
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }

      // Update height observer to watch the new current slide after transition completes
      tl.call(() => {
        // Small delay to ensure slide is fully positioned
        setTimeout(() => {
          setupHeightObserver();
          // Update container height based on actual rendered height (handles dynamic content)
          updateContainerHeight(true);
          
          // Trigger animations for elements in the active slide after slide is fully visible
          setTimeout(() => {
            triggerSlideAnimations(nextSlide);
          }, 50);
          
          // Check if new slide has autoplay class and start/stop autoplay accordingly
          checkAndUpdateAutoplay();
        }, 20);
      });
    }
    
    // Start autoplay if current slide has autoplay class
    function startAutoplay() {
      // Clear any existing interval
      stopAutoplay();
      
      const currentSlide = slides[currentIndex];
      if (!currentSlide || !currentSlide.classList.contains('autoplay')) {
        return;
      }
      
      // Only autoplay if not on last slide
      if (currentIndex < slides.length - 1) {
        autoplayInterval = setInterval(() => {
          nextSlide();
        }, autoplayDelay);
      }
    }
    
    // Stop autoplay
    function stopAutoplay() {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
    }
    
    // Check current slide and update autoplay
    function checkAndUpdateAutoplay() {
      const currentSlide = slides[currentIndex];
      if (currentSlide && currentSlide.classList.contains('autoplay')) {
        startAutoplay();
      } else {
        stopAutoplay();
      }
    }

    // Next slide
    function nextSlide() {
      if (currentIndex < slides.length - 1) {
        goToSlide(currentIndex + 1);
      }
    }

    // Previous slide
    function prevSlide() {
      if (currentIndex > 0) {
        goToSlide(currentIndex - 1);
      }
    }

    // Event listeners
    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        stopAutoplay(); // Stop autoplay on manual navigation
        nextSlide();
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        stopAutoplay(); // Stop autoplay on manual navigation
        prevSlide();
      });
    }

    // Bullet navigation
    bullets.forEach((bullet, index) => {
      bullet.addEventListener("click", () => {
        stopAutoplay(); // Stop autoplay on manual navigation
        goToSlide(index);
      });
    });

    // Text link navigation
    textLinks.forEach((link, index) => {
      const slideIndex =
        parseInt(link.getAttribute("data-slide-index")) || index;

      link.addEventListener("click", () => {
        stopAutoplay(); // Stop autoplay on manual navigation
        goToSlide(slideIndex);
      });

      // Also handle keyboard navigation
      link.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          goToSlide(slideIndex);
        }
      });
    });

    // Keyboard navigation
    wrapper.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") {
        stopAutoplay(); // Stop autoplay on manual navigation
        prevSlide();
      }
      if (e.key === "ArrowRight") {
        stopAutoplay(); // Stop autoplay on manual navigation
        nextSlide();
      }
    });

    // Initialize arrow states
    updateArrows();

    // Setup height observer for initial slide
    setupHeightObserver();
    
    // Trigger animations for first slide on initial load
    // Use multiple attempts to catch blocks that initialize late
    if (slides.length > 0) {
      const triggerFirstSlide = (attempt = 0) => {
        triggerSlideAnimations(slides[0]);
        if (attempt < 5) {
          // Retry a few times in case blocks are still initializing
          setTimeout(() => triggerFirstSlide(attempt + 1), 300);
        }
      };
      setTimeout(() => triggerFirstSlide(), 200);
    }
    
    // Check and start autoplay if first slide has autoplay class
    checkAndUpdateAutoplay();
    
    // Pause autoplay when carousel is out of view, resume when in view
    ScrollTrigger.create({
      trigger: wrapper,
      start: "top bottom",
      end: "bottom top",
      onEnter: () => {
        checkAndUpdateAutoplay();
      },
      onEnterBack: () => {
        checkAndUpdateAutoplay();
      },
      onLeave: () => {
        stopAutoplay();
      },
      onLeaveBack: () => {
        stopAutoplay();
      }
    });

    // Make wrapper focusable for keyboard navigation
    wrapper.setAttribute("tabindex", "0");
  });

  console.log(`[SOJ] Initialized ${carouselWrappers.length} GSAP carousel(s)`);
}
